import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { TAutocompleteOption } from '~/components/AutocompleteField/types.ts';
import { errorMessages } from '~/constants/errorMessages.ts';
import api from '~/libs/api';
import type { TDictionaryCountry, TDictionaryCountrySearchResponse } from './types.ts';

const mapCountryToOption = (country?: TDictionaryCountry | null): TAutocompleteOption | null => {
  if (!country?.k040) {
    return null;
  }

  return {
    value: country.k040,
    label: country.name,
  };
};

const getCountries = (name: string) => async () => {
  try {
    const { data } = await api.get<TDictionaryCountrySearchResponse>(
      '/dictionary-service/country/search',
      {
        params: {
          name: name.trim() || undefined,
          page: 0,
          size: 20,
        },
      }
    );

    return data;
  } catch {
    throw Error(errorMessages.SERVICE_UNAVAILABLE);
  }
};

const getCountryById = (id: string) => async () => {
  try {
    const { data } = await api.get<TDictionaryCountry>(`/dictionary-service/country/${id}`);

    return data;
  } catch {
    throw Error(errorMessages.SERVICE_UNAVAILABLE);
  }
};

export const useCountrySearchQuery = (name: string) => {
  const { data, error, isLoading, isError } = useQuery<TDictionaryCountrySearchResponse>({
    queryKey: ['country-search', name],
    queryFn: getCountries(name),
    staleTime: 30 * 60 * 1000,
  });

  const countryOptions: TAutocompleteOption[] = useMemo(
    () =>
      (data?.content ?? [])
        .map(mapCountryToOption)
        .filter((option): option is TAutocompleteOption => !!option),
    [data]
  );

  return { countryOptions, countries: data?.content ?? [], error, isLoading, isError };
};

export const useCountryByIdQuery = (id?: string | number | null) => {
  const normalizedId = String(id ?? '').trim();
  const { data, error, isLoading, isError } = useQuery<TDictionaryCountry>({
    queryKey: ['country', normalizedId],
    queryFn: getCountryById(normalizedId),
    enabled: !!normalizedId,
    staleTime: 30 * 60 * 1000,
  });

  const countryOption = useMemo(() => mapCountryToOption(data), [data]);

  return { countryOption, country: data, error, isLoading, isError };
};
